import { useState } from 'react';
import { Radio, Truck, UserCheck, Users } from 'lucide-react';
import { VENDOR_DRIVERS, VENDOR_FLEET } from './mockVendorData';

function Toggle({ enabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={onChange}
      className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
        enabled ? 'bg-emerald-600' : 'bg-logi-border'
      }`}
    >
      <span
        className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          enabled ? 'left-5' : 'left-0.5'
        }`}
      />
    </button>
  );
}

export default function AvailabilityUpdater() {
  const [fleet, setFleet] = useState(VENDOR_FLEET);
  const [drivers, setDrivers] = useState(VENDOR_DRIVERS);
  const [synced, setSynced] = useState(false);

  const toggleFleet = (id) => {
    setFleet((prev) =>
      prev.map((v) => (v.id === id ? { ...v, available: !v.available } : v))
    );
    setSynced(false);
  };

  const toggleDriver = (id) => {
    setDrivers((prev) =>
      prev.map((d) => (d.id === id ? { ...d, available: !d.available } : d))
    );
    setSynced(false);
  };

  const handleSync = () => {
    setSynced(true);
    setTimeout(() => setSynced(false), 3000);
  };

  const availableTrucks = fleet.filter((v) => v.available).length;
  const availableDrivers = drivers.filter((d) => d.available).length;

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 animate-pulse text-emerald-700" />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Fleet &amp; Driver Availability Updater</h3>
            <p className="text-[11px] text-logi-text0">Real-time sync to OGTS dispatch grid</p>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="rounded-lg bg-white px-2 py-1 text-logi-muted">
            <Truck className="mr-1 inline h-3 w-3 text-logi-accent" />
            {availableTrucks}/{fleet.length} trucks
          </span>
          <span className="rounded-lg bg-white px-2 py-1 text-logi-muted">
            <Users className="mr-1 inline h-3 w-3 text-logi-accent" />
            {availableDrivers}/{drivers.length} drivers
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
        {/* Fleet toggles */}
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            <Truck className="h-3.5 w-3.5" />
            Active Trucks
          </p>
          <div className="space-y-2">
            {fleet.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between rounded-xl border border-logi-grid bg-white px-3 py-2.5"
              >
                <div>
                  <p className="font-mono text-sm font-semibold text-logi-accent">{v.vehicleNumber}</p>
                  <p className="text-[10px] text-logi-text0">
                    {v.type} · {v.driver}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-bold uppercase ${
                      v.available ? 'text-emerald-700' : 'text-logi-text0'
                    }`}
                  >
                    {v.available ? 'Available' : 'Unavailable'}
                  </span>
                  <Toggle
                    enabled={v.available}
                    onChange={() => toggleFleet(v.id)}
                    label={`Toggle ${v.vehicleNumber}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Driver toggles */}
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            <UserCheck className="h-3.5 w-3.5" />
            Active Drivers
          </p>
          <div className="space-y-2">
            {drivers.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between rounded-xl border border-logi-grid bg-white px-3 py-2.5"
              >
                <div>
                  <p className="text-sm font-semibold text-logi-text">{d.name}</p>
                  <p className="text-[10px] capitalize text-logi-text0">{d.status.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-bold uppercase ${
                      d.available ? 'text-emerald-700' : 'text-logi-text0'
                    }`}
                  >
                    {d.available ? 'Available' : 'Unavailable'}
                  </span>
                  <Toggle
                    enabled={d.available}
                    onChange={() => toggleDriver(d.id)}
                    label={`Toggle ${d.name}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-logi-grid px-4 py-3">
        <button
          type="button"
          onClick={handleSync}
          className={`w-full rounded-xl py-2.5 text-sm font-bold transition ${
            synced
              ? 'bg-emerald-600/20 text-emerald-700 ring-1 ring-emerald-500/30'
              : 'bg-indigo-600 text-white hover:bg-indigo-500'
          }`}
        >
          {synced ? '✓ Synced to OGTS Availability Engine' : 'Push Updates to Dispatch Grid'}
        </button>
      </div>
    </section>
  );
}
